import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import '../../../styles/aida-base.css';
import '../../../styles/aida-responsive.css';

/**
 * DevExplore Component
 * Four alternating sections exploring the Playcast developer value proposition:
 *   1. We Create Ads For You
 *   2. Demos Without Downloads
 *   3. Bringing All Games to Mobile
 *   4. Expand Your Outreach
 * Desktop: absolute-positioned 1024px canvas (hidden lg:block)
 * Mobile: vertically stacked cards (flex lg:hidden)
 * Transparent background — inherits from page background zones.
 */

const WHITE = 'rgba(255, 255, 255, 1)';

const CARD_BG =
  'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5951509/image-5ad86f85-76b3-415e-8cdd-a37c698c6890.png?w=535&e=webp&nll=true&cX=389&cY=231&cW=439&cH=356';
const MOB_CARD_BG =
  'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5951509/image-5ad86f85-76b3-415e-8cdd-a37c698c6890.png?w=859&cX=399&cY=231&cW=418&cH=356';

const VIDEO_1 = 'https://i.imgur.com/RJUgeti.gif';
const VIDEO_2 = 'https://i.imgur.com/79a5YKm.gif';
const VIDEO_3 = 'https://i.imgur.com/ILPDNy9.gif';
const VIDEO_4 = 'https://i.imgur.com/dZTgBCz.gif';

const DECOR_SPRITE =
  'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5951509/image-2990fe30-8f7b-4049-aad1-6f7c843aabd7.png';
const DECOR_A = `${DECOR_SPRITE}?w=148&e=webp&nll=true&cX=126&cY=1379&cW=916&cH=708`;   // S1 + S4 mirrored
const DECOR_B = `${DECOR_SPRITE}?w=103&e=webp&nll=true&cX=2554&cY=25&cW=916&cH=657`;    // S1 + S2 mirrored
const DECOR_C = `${DECOR_SPRITE}?w=54&e=webp&nll=true&cX=4420&cY=916&cW=474&cH=812`;    // S2 mirrored
const DECOR_D = `${DECOR_SPRITE}?w=148&e=webp&nll=true&cX=0&cY=2068&cW=916&cH=902`;     // S3
const DECOR_E = `${DECOR_SPRITE}?w=68&e=webp&nll=true&cX=2709&cY=0&cW=397&cH=355`;      // S3
const DECOR_F = `${DECOR_SPRITE}?w=99&e=webp&nll=true&cX=2665&cY=0&cW=611&cH=430`;      // S4 mirrored

// All decorative images use the same breathing rock animation
const ROCK_ANIMATE = { rotate: [-5, 5, -5] as [number, number, number] };
const ROCK_TRANSITION = { duration: 6, repeat: Infinity, ease: 'easeInOut' as const };

// Mobile decor positions: desktop card is 239×193, mobile card bg is 300×255 (top:6px offset)
// Scale: x*1.255, y*1.321 relative to card top-left, then add 6px for the top offset
const MOBILE_DECORS: { src: string; left: number; top: number; width: number; height: number; imgTransform?: string; zIndex?: number }[][] = [
  // Section 1 — right side (+20 right, +40 down)
  [
    { src: DECOR_A, left: 253, top: 194, width: 83, height: 67, imgTransform: 'scaleX(-1)' },
    { src: DECOR_B, left: 253, top: 257, width: 58, height: 44, imgTransform: 'scaleX(-1)' },
  ],
  // Section 2 — left side (-20 left, +40 down, -30 up)
  [
    { src: DECOR_C, left: 5,  top: 217, width: 30, height: 54 },
    { src: DECOR_B, left: 35, top: 260, width: 58, height: 44 },
  ],
  // Section 3 — right side (-20 left, -10 up, behind card)
  [
    { src: DECOR_D, left: 258, top: 192, width: 83, height: 86, imgTransform: 'scaleX(-1)', zIndex: 0 },
    { src: DECOR_E, left: 232, top: 266, width: 38, height: 36, imgTransform: 'scaleX(-1)', zIndex: 0 },
  ],
  // Section 4 — left side (-20 left, +40 down)
  [
    { src: DECOR_A, left: -19, top: 206, width: 83, height: 67 },
    { src: DECOR_F, left: 9,   top: 273, width: 55, height: 41 },
  ],
];

const h1Style = {
  lineHeight: '30px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 900,
  fontSize: '28px',
  fontStyle: 'italic',
  color: WHITE,
  margin: 0,
  fontVariationSettings: "'wght' 900",
};

const bodyStyle = {
  lineHeight: '12px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '9px',
  color: 'rgba(255,255,255,0.85)',
  margin: 0,
};

const cardBaseStyle = {
  backgroundImage: `url("${CARD_BG}")`,
  backgroundPosition: '50% 50%' as const,
  backgroundSize: 'cover' as const,
  borderRadius: '120px 10px',
  filter: 'drop-shadow(rgba(3, 10, 42, 0.41) -5px -5px 12px)',
};

// All design coordinates are offset by -576 (the topmost canvas y in the design)
export const DevExplore = () => {
  const desktopRef = useRef(null);
  const mobileRef = useRef(null);
  const isInView = useInView(desktopRef, { once: true, margin: '-100px' });
  const isMobileInView = useInView(mobileRef, { once: true, amount: 0.1 });

  return (
    <>
      {/* ── Desktop Layout ── */}
      <motion.section
        ref={desktopRef}
        className="relative mx-auto hidden w-full lg:block"
        style={{ width: '100%', height: '1060px', backgroundColor: 'transparent', overflow: 'visible', position: 'relative' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <motion.div
          style={{ position: 'relative', width: '100%', height: '100%' }}
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          {/* ── Main Title ── */}
          <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: '0px', width: '533px', zIndex: 385, textAlign: 'center' }}>
            <p style={{ lineHeight: '48px', fontFamily: 'Inter, sans-serif', fontWeight: 900, fontSize: '40px', fontStyle: 'italic', letterSpacing: '-0.5px', color: WHITE, margin: 0, fontVariationSettings: "'wght' 900" }}>
              EXPLORE THE PROS
            </p>
            <p style={{ lineHeight: '48px', fontFamily: 'Inter, sans-serif', fontWeight: 900, fontSize: '40px', fontStyle: 'italic', letterSpacing: '-0.5px', color: WHITE, margin: 0, fontVariationSettings: "'wght' 900" }}>
              OF PLAYCAST
            </p>
          </div>

          {/* ── Section 1: We Create Ads For You ── */}

          {/* Card */}
          <div style={{ position: 'absolute', left: '247px', top: '135px', width: '239px', height: '193px', zIndex: 396, ...cardBaseStyle, transform: 'rotate(180deg) scaleX(-1)' }} />

          {/* Video */}
          <div style={{ position: 'absolute', left: '245px', top: '164px', width: '231px', height: '134px', zIndex: 397, borderRadius: '20px', overflow: 'hidden' }}>
            <img src={VIDEO_1} alt="" className="fixMobileImageStretch" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '20px' }} draggable={false} />
          </div>

          {/* Title */}
          <div style={{ position: 'absolute', left: '548px', top: '176px', width: '282px', zIndex: 377 }}>
            <h1 style={h1Style}>WE CREATE ADS FOR YOU</h1>
          </div>

          {/* Body */}
          <div style={{ position: 'absolute', left: '548px', top: '247px', width: '225px', zIndex: 376 }}>
            <p style={bodyStyle}>Players click on a Playcast-created ad for your game on any social media platform.</p>
          </div>

          {/* Decor S1-1 */}
          <motion.div style={{ position: 'absolute', left: '234px', top: '247px', width: '66px', height: '51px', zIndex: 407 }} animate={ROCK_ANIMATE} transition={ROCK_TRANSITION}>
            <img src={DECOR_A} alt="" className="fixMobileImageStretch" style={{ width: '100%', height: '100%', objectFit: 'contain' }} draggable={false} />
          </motion.div>

          {/* Decor S1-2 */}
          <motion.div style={{ position: 'absolute', left: '254px', top: '295px', width: '46px', height: '33px', zIndex: 408 }} animate={ROCK_ANIMATE} transition={ROCK_TRANSITION}>
            <img src={DECOR_B} alt="" className="fixMobileImageStretch" style={{ width: '100%', height: '100%', objectFit: 'contain' }} draggable={false} />
          </motion.div>

          {/* ── Section 2: Demos Without Downloads ── */}

          {/* Card */}
          <div style={{ position: 'absolute', left: '548px', top: '358px', width: '239px', height: '193px', zIndex: 394, ...cardBaseStyle, transform: 'rotate(-180deg)' }} />

          {/* Video */}
          <div style={{ position: 'absolute', left: '567px', top: '358px', width: '200px', height: '187px', zIndex: 395, borderRadius: '20px', overflow: 'hidden' }}>
            <img src={VIDEO_2} alt="" className="fixMobileImageStretch" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '20px' }} draggable={false} />
          </div>

          {/* Title */}
          <div style={{ position: 'absolute', left: '203px', top: '406px', width: '282px', zIndex: 387, textAlign: 'right' }}>
            <h1 style={h1Style}>DEMOS WITHOUT</h1>
            <h1 style={h1Style}>DOWNLOADS</h1>
          </div>

          {/* Body */}
          <div style={{ position: 'absolute', left: '260px', top: '477px', width: '225px', zIndex: 386, textAlign: 'right' }}>
            <p style={bodyStyle}>Players can enter your game directly from the ads, no download or install necessary.</p>
          </div>

          {/* Decor S2-1 */}
          <motion.div style={{ position: 'absolute', left: '743px', top: '510px', width: '24px', height: '41px', zIndex: 413 }} animate={ROCK_ANIMATE} transition={ROCK_TRANSITION}>
            <img src={DECOR_C} alt="" className="fixMobileImageStretch" style={{ width: '100%', height: '100%', objectFit: 'contain', transform: 'scaleX(-1)' }} draggable={false} />
          </motion.div>

          {/* Decor S2-2 */}
          <motion.div style={{ position: 'absolute', left: '697px', top: '543px', width: '46px', height: '33px', zIndex: 411 }} animate={ROCK_ANIMATE} transition={ROCK_TRANSITION}>
            <img src={DECOR_B} alt="" className="fixMobileImageStretch" style={{ width: '100%', height: '100%', objectFit: 'contain', transform: 'scaleX(-1)' }} draggable={false} />
          </motion.div>

          {/* ── Section 3: Bringing All Games to Mobile ── */}

          {/* Card */}
          <div style={{ position: 'absolute', left: '247px', top: '597px', width: '239px', height: '193px', zIndex: 400, ...cardBaseStyle, transform: 'rotate(180deg) scaleX(-1)' }} />

          {/* Video */}
          <div style={{ position: 'absolute', left: '289px', top: '644px', width: '155px', height: '99px', zIndex: 401, borderRadius: '20px', overflow: 'hidden' }}>
            <img src={VIDEO_3} alt="" className="fixMobileImageStretch" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '20px' }} draggable={false} />
          </div>

          {/* Title */}
          <div style={{ position: 'absolute', left: '548px', top: '646px', width: '282px', zIndex: 388 }}>
            <h1 style={h1Style}>BRINGING ALL GAMES TO MOBILE</h1>
          </div>

          {/* Body */}
          <div style={{ position: 'absolute', left: '548px', top: '715px', width: '225px', zIndex: 393 }}>
            <p style={bodyStyle}>Bring any game to phones, regardless of the developer or platform.</p>
          </div>

          {/* Decor S3-1 */}
          <motion.div style={{ position: 'absolute', left: '214px', top: '715px', width: '66px', height: '65px', zIndex: 412 }} animate={ROCK_ANIMATE} transition={ROCK_TRANSITION}>
            <img src={DECOR_D} alt="" className="fixMobileImageStretch" style={{ width: '100%', height: '100%', objectFit: 'contain' }} draggable={false} />
          </motion.div>

          {/* Decor S3-2 */}
          <motion.div style={{ position: 'absolute', left: '271px', top: '771px', width: '30px', height: '27px', zIndex: 414 }} animate={ROCK_ANIMATE} transition={ROCK_TRANSITION}>
            <img src={DECOR_E} alt="" className="fixMobileImageStretch" style={{ width: '100%', height: '100%', objectFit: 'contain' }} draggable={false} />
          </motion.div>

          {/* ── Section 4: Expand Your Outreach ── */}

          {/* Card */}
          <div style={{ position: 'absolute', left: '548px', top: '825px', width: '239px', height: '193px', zIndex: 398, ...cardBaseStyle, transform: 'rotate(-180deg)' }} />

          {/* Video */}
          <div style={{ position: 'absolute', left: '536px', top: '845px', width: '261px', height: '151px', zIndex: 399, borderRadius: '20px', overflow: 'hidden' }}>
            <img src={VIDEO_4} alt="" className="fixMobileImageStretch" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '20px' }} draggable={false} />
          </div>

          {/* Title */}
          <div style={{ position: 'absolute', left: '203px', top: '871px', width: '282px', zIndex: 392, textAlign: 'right' }}>
            <h1 style={h1Style}>EXPAND YOUR OUTREACH</h1>
          </div>

          {/* Body */}
          <div style={{ position: 'absolute', left: '260px', top: '940px', width: '225px', zIndex: 391, textAlign: 'right' }}>
            <p style={bodyStyle}>Connect with unexpected audiences by building name recognition via instant demo experience.</p>
          </div>

          {/* Decor S4-1 */}
          <motion.div style={{ position: 'absolute', left: '720px', top: '946px', width: '66px', height: '51px', zIndex: 410 }} animate={ROCK_ANIMATE} transition={ROCK_TRANSITION}>
            <img src={DECOR_A} alt="" className="fixMobileImageStretch" style={{ width: '100%', height: '100%', objectFit: 'contain', transform: 'scaleX(-1)' }} draggable={false} />
          </motion.div>

          {/* Decor S4-2 */}
          <motion.div style={{ position: 'absolute', left: '720px', top: '997px', width: '44px', height: '31px', zIndex: 415 }} animate={ROCK_ANIMATE} transition={ROCK_TRANSITION}>
            <img src={DECOR_F} alt="" className="fixMobileImageStretch" style={{ width: '100%', height: '100%', objectFit: 'contain', transform: 'scaleX(-1)' }} draggable={false} />
          </motion.div>

        </motion.div>
      </motion.section>

      {/* ── Mobile Layout ── */}
      <motion.section
        ref={mobileRef}
        className="relative flex w-full flex-col items-center lg:hidden"
        style={{ backgroundColor: 'transparent', padding: '20px 16px 40px', gap: '48px' }}
        initial={{ opacity: 0 }}
        animate={isMobileInView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <p style={{ lineHeight: '44px', fontFamily: 'Inter, sans-serif', fontWeight: 900, fontSize: '36px', fontStyle: 'italic', letterSpacing: '-0.5px', color: WHITE, margin: 0, textAlign: 'center', fontVariationSettings: "'wght' 900" }}>
          EXPLORE THE PROS<br />OF PLAYCAST
        </p>
        {[
          { video: VIDEO_1, title: 'WE CREATE ADS FOR YOU', body: 'Players click on a Playcast-created ad for your game on any social media platform.' },
          { video: VIDEO_2, title: 'DEMOS WITHOUT DOWNLOADS', body: 'Players can enter your game directly from the ads, no download or install necessary.' },
          { video: VIDEO_3, title: 'BRINGING ALL GAMES TO MOBILE', body: 'Bring any game to phones, regardless of the developer or platform.' },
          { video: VIDEO_4, title: 'EXPAND YOUR OUTREACH', body: 'Connect with unexpected audiences by building name recognition via instant demo experience.' },
        ].map(({ video, title, body }, index) => {
          const isAlt = index % 2 === 1;
          return (
          <div key={title} style={{ display: 'flex', flexDirection: 'column', width: '300px' }}>
            {/* Image area — card bg + video overlay */}
            <div style={{ position: 'relative', width: '300px', height: '285px', overflow: 'visible' }}>
              {/* Card background — alternates rotation so big corner swaps sides */}
              <div style={{
                position: 'absolute', left: '0px', top: '6px',
                width: '300px', height: '255px',
                transform: isAlt ? 'scaleX(-1)' : 'rotate(-180deg)',
                filter: 'drop-shadow(rgba(3, 10, 42, 0.41) -5px -5px 12px)',
                backgroundImage: `url("${MOB_CARD_BG}")`,
                backgroundPosition: '50% 50%', backgroundSize: 'cover',
                borderRadius: '120px 10px',
                zIndex: index === 2 ? 1 : undefined,
              }} />
              {/* Video */}
              {(() => {
                const vw = index === 0 || index === 3 ? 206 : 283;
                const vh = index === 0 || index === 3 ? 209 : 285;
                const vl = (300 - vw) / 2;
                // Vertically center all videos on the card background (top:6, height:255 → center at 133.5px)
                const vt = 133.5 - vh / 2;
                return (
                  <div style={{ position: 'absolute', left: `${vl}px`, top: `${vt}px`, width: `${vw}px`, height: `${vh}px`, borderRadius: '20px', overflow: 'hidden', zIndex: index === 2 ? 2 : undefined }}>
                    <img src={video} alt="" draggable={false} style={{ width: '100%', height: '100%', objectFit: index === 2 ? 'contain' : 'cover', display: 'block', borderRadius: '20px' }} />
                  </div>
                );
              })()}
              {/* Animated decor sprites */}
              {MOBILE_DECORS[index].map((decor, di) => (
                <motion.div
                  key={di}
                  style={{ position: 'absolute', left: `${decor.left}px`, top: `${decor.top}px`, width: `${decor.width}px`, height: `${decor.height}px`, zIndex: decor.zIndex ?? 10 }}
                  animate={ROCK_ANIMATE}
                  transition={ROCK_TRANSITION}
                >
                  <img src={decor.src} alt="" draggable={false} style={{ width: '100%', height: '100%', objectFit: 'contain', ...(decor.imgTransform ? { transform: decor.imgTransform } : {}) }} />
                </motion.div>
              ))}
            </div>
            {/* Title */}
            <h3 style={{ lineHeight: '31px', fontFamily: 'Inter, sans-serif', fontWeight: 900, fontSize: '30px', fontStyle: 'normal', color: WHITE, margin: 0, marginTop: '16px', fontVariationSettings: "'wght' 900", width: '300px', textAlign: isAlt ? 'right' : 'left' }}>{title}</h3>
            {/* Body */}
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px', lineHeight: '22px', color: WHITE, margin: 0, marginTop: '10px', width: '300px', textAlign: isAlt ? 'right' : 'left' }}>{body}</p>
          </div>
          );
        })}
      </motion.section>
    </>
  );
};
